import Component from '@glimmer/component';
import { uniqueId } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import type { ComponentLike } from '@glint/template';
import { PhArrowRight, PhX } from 'ember-phosphor-icons';
import Button from 'tio-ui/components/button';
import Chip, { type ChipIntent } from 'tio-ui/components/chip';

/**
 * How urgent a row is. The caller decides from its own deadline rules; this
 * component only maps the level to the Figma red / yellow / green treatment.
 * `info` is for notifications that ask nothing of the user.
 */
export type TaskUrgency = 'urgent' | 'soon' | 'onTrack' | 'info';

const TILE_CLASSES: Record<TaskUrgency, string> = {
  urgent: 'tio-bg-danger-soft tio-fg-danger',
  soon: 'tio-bg-warning-soft tio-fg-warning',
  onTrack: 'tio-bg-success-soft tio-fg-success',
  info: 'tio-bg-info-soft tio-fg-info',
};

const CHIP_INTENTS: Record<TaskUrgency, ChipIntent> = {
  urgent: 'danger',
  soon: 'warning',
  onTrack: 'success',
  info: 'default',
};

interface TaskCardBaseArgs {
  /** The row's h3 (rows sit under the panel's h2); says what needs doing or what happened. */
  title: string;
  /** One line on what to do, or (for `info`) what happened and that no action is needed. */
  description: string;
  /** Glyph in the urgency-tinted tile; picks the kind of task, not its urgency. */
  icon: ComponentLike<{ Element: SVGElement }>;
  /** Drives the tile and chip colour. */
  urgency: TaskUrgency;
  /** Already-formatted deadline text, e.g. "due 10/14"; the chip is omitted without it. */
  dueLabel?: string;
}

/**
 * A row ends in an action link, an action button, a dismiss button, or nothing;
 * never more than one. A dismissible row must also name its dismiss button.
 */
type TaskCardTrailingArgs =
  | {
      actionLabel?: undefined;
      actionRoute?: undefined;
      actionModel?: undefined;
      onAction?: undefined;
      onDismiss?: undefined;
      dismissLabel?: undefined;
    }
  | {
      /** Trailing "Action →" link text. */
      actionLabel: string;
      /** Route the action link opens. */
      actionRoute: string;
      /** Dynamic segment for `@actionRoute`, when the route has one. */
      actionModel?: string | number;
      onAction?: undefined;
      onDismiss?: undefined;
      dismissLabel?: undefined;
    }
  | {
      /** Trailing "Action →" button text. */
      actionLabel: string;
      actionRoute?: undefined;
      actionModel?: undefined;
      /** Opens something in place (a modal, a drawer) instead of navigating. */
      onAction: () => void;
      onDismiss?: undefined;
      dismissLabel?: undefined;
    }
  | {
      actionLabel?: undefined;
      actionRoute?: undefined;
      actionModel?: undefined;
      onAction?: undefined;
      /** Shows a dismiss (X) button, for notifications that need no action. */
      onDismiss: () => void;
      /** Accessible name for the dismiss button; the row title is added as its description. */
      dismissLabel: string;
    };

interface TaskCardSignature {
  Args: TaskCardBaseArgs & TaskCardTrailingArgs;
  Element: HTMLLIElement;
}

/**
 * One row of the Figma `Task Cards/horizontal` list. Renders an `<li>`, so it
 * belongs inside an `AttentionPanel` (or another `<ul>`). The action link (or
 * button) is described by the row title so repeated link text stays distinguishable.
 */
export default class TaskCard extends Component<TaskCardSignature> {
  get tileClass() {
    return TILE_CLASSES[this.args.urgency];
  }

  get chipIntent() {
    return CHIP_INTENTS[this.args.urgency];
  }

  get actionModels() {
    const { actionModel } = this.args;
    return actionModel === undefined ? [] : [actionModel];
  }

  <template>
    {{#let (uniqueId) as |titleId|}}
      {{! flex-wrap + the text column's minimum width: on a phone the trailing action drops to its own line instead of squeezing the copy. }}
      <li
        class="tio-row flex-wrap tio-card tio-card--radius-card tio-card--padding-sm border tio-border-default"
        ...attributes
      >
        <div class="tio-row-center h-10 w-10 shrink-0 rounded-lg {{this.tileClass}}">
          {{#let @icon as |Icon|}}
            <Icon class="tio-icon-lg" />
          {{/let}}
        </div>
        <div class="tio-col-extra-tight tio-fill-min min-w-48">
          <div class="tio-row-tight flex-wrap">
            <h3 id={{titleId}} class="tio-text-base-semibold tio-fg-primary">{{@title}}</h3>
            {{#if @dueLabel}}
              <Chip @intent={{this.chipIntent}} @appearance="faded" @size="sm">{{@dueLabel}}</Chip>
            {{/if}}
          </div>
          <p class="tio-text-base-medium tio-fg-secondary">{{@description}}</p>
        </div>
        {{#if @actionRoute}}
          {{#if @actionLabel}}
            <LinkTo
              @route={{@actionRoute}}
              @models={{this.actionModels}}
              class="tio-row-tight shrink-0 tio-text-sm-semibold app-text-link"
              aria-describedby={{titleId}}
            >
              {{@actionLabel}}
              <PhArrowRight class="tio-icon-md" />
            </LinkTo>
          {{/if}}
        {{else if @onAction}}
          <Button
            @appearance="link"
            @size="sm"
            class="tio-row-tight shrink-0 tio-text-sm-semibold"
            aria-describedby={{titleId}}
            data-test-dashboard-kit-task-action
            {{on "click" @onAction}}
          >
            {{@actionLabel}}
            <PhArrowRight class="tio-icon-md" />
          </Button>
        {{/if}}
        {{#if @onDismiss}}
          <Button
            @appearance="minimal"
            @size="sm"
            @icon={{PhX}}
            @ariaLabel={{@dismissLabel}}
            class="shrink-0"
            aria-describedby={{titleId}}
            {{on "click" @onDismiss}}
          />
        {{/if}}
      </li>
    {{/let}}
  </template>
}
